import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import theme from '../styles/theme';
import { useLocalStorage } from '../utils/hooks';
import API_URL from '../config/api';

const AlignmentPage = () => {
  console.log('AlignmentPage mounted');

  const [apImage, setApImage] = useState(null);
  const [latImage, setLatImage] = useState(null);
  const [apImageDims, setApImageDims] = useState(null);
  const [latImageDims, setLatImageDims] = useState(null);
  const [showLines, setShowLines] = useState(true);
  const [showMeasurements, setShowMeasurements] = useState(true);
  const [loading, setLoading] = useState({
    ap: true,
    lat: true
  });
  const [errors, setErrors] = useState({
    ap: null,
    lat: null
  });
  const [xrayMeasurements, setXrayMeasurements] = useState(null);
  const [xrayKeypoints, setXrayKeypoints] = useState(null);
  const [measurementsLoading, setMeasurementsLoading] = useState(true);
  const [adjustedMeasurements, setAdjustedMeasurements] = useState(null);
  const [adjustedLoading, setAdjustedLoading] = useState(false);
  const [selectedLevels] = useLocalStorage('anatomical-selected-rows', []);

  // Build parameter data from measurements
  const buildParameterData = () => {
    if (!xrayMeasurements) {
      // Return default values if no measurements
      return {
        sagittalVertebralAxis: [
          { label: 'C7 plumb line', current: '-', proposed: '-' },
        ],
        cobbAngle: [
          { label: 'Cobb Angle', current: '-', proposed: '-' },
        ],
        lumbar: [
          { label: 'Lumbar Lordosis', current: '-', proposed: '-' },
          { label: 'L1-L2', current: '-', proposed: '-', isSegmental: true },
          { label: 'L2-L3', current: '-', proposed: '-', isSegmental: true },
          { label: 'L3-L4', current: '-', proposed: '-', isSegmental: true },
          { label: 'L4-L5', current: '-', proposed: '-', isSegmental: true },
          { label: 'L5-S1', current: '-', proposed: '-', isSegmental: true },
        ],
        sacroPelvic: [
          { label: 'Sacral Slope', current: '-', proposed: '-' },
          { label: 'Pelvic Incidence', current: '-', proposed: '-' },
          { label: 'Pelvic Tilt', current: '-', proposed: '-' },
        ],
        other: [
          { label: 'PI-LL Mismatch', current: '-', proposed: '-' },
        ],
      };
    }

    // Destructure measurements with originals
    const { ap, lat, combined, original_ap, original_lat, original_combined } = xrayMeasurements;

    // Use original values for "Current" column, fallback to main if original not explicit
    const currentLat = original_lat || lat;
    const currentCombined = original_combined || combined;
    const currentAp = original_ap || ap;

    // Use computed adjustments for "Proposed" column
    const proposedLat = adjustedMeasurements?.lat;
    const proposedCombined = adjustedMeasurements?.combined;
    const proposedAp = adjustedMeasurements?.ap;

    // Helper function to format measurement value
    const formatValue = (measurement) => {
      if (!measurement) return '-';
      const unit = measurement.unit === 'degrees' ? '°' : measurement.unit === 'cm' ? 'cm' : 'mm';
      return `${measurement.value}${unit}`;
    };

    // Convert SVA from cm to mm for display
    const formatSVA = (measurement) => {
      if (!measurement) return '-';
      // Convert cm to mm
      const mmValue = measurement.value * 10;
      return `${Math.round(mmValue)}mm`;
    };

    // Build the parameter structure with requested changes
    const params = {
      sagittalVertebralAxis: [
        {
          label: 'C7 plumb line',
          current: currentLat?.sva ? formatSVA(currentLat.sva) : '-',
          proposed: proposedLat?.sva ? formatSVA(proposedLat.sva) : '-'
        },
      ],
      cobbAngle: [
        {
          label: 'Cobb Angle',
          current: currentAp?.cobb_angle ? formatValue(currentAp.cobb_angle) : '-',
          proposed: proposedAp?.cobb_angle ? formatValue(proposedAp.cobb_angle) : '-'
        },
      ],
      lumbar: [
        {
          label: 'Lumbar Lordosis',
          current: currentLat?.lumbar_lordosis ? formatValue(currentLat.lumbar_lordosis) : '-',
          proposed: proposedLat?.lumbar_lordosis ? formatValue(proposedLat.lumbar_lordosis) : '-'
        },
        {
          label: 'L1-L2',
          current: currentLat?.segmental_angles?.['L1-L2'] ? formatValue(currentLat.segmental_angles['L1-L2']) : '-',
          proposed: proposedLat?.segmental_angles?.['L1-L2'] ? formatValue(proposedLat.segmental_angles['L1-L2']) : '-',
          isSegmental: true
        },
        {
          label: 'L2-L3',
          current: currentLat?.segmental_angles?.['L2-L3'] ? formatValue(currentLat.segmental_angles['L2-L3']) : '-',
          proposed: proposedLat?.segmental_angles?.['L2-L3'] ? formatValue(proposedLat.segmental_angles['L2-L3']) : '-',
          isSegmental: true
        },
        {
          label: 'L3-L4',
          current: currentLat?.segmental_angles?.['L3-L4'] ? formatValue(currentLat.segmental_angles['L3-L4']) : '-',
          proposed: proposedLat?.segmental_angles?.['L3-L4'] ? formatValue(proposedLat.segmental_angles['L3-L4']) : '-',
          isSegmental: true
        },
        {
          label: 'L4-L5',
          current: currentLat?.segmental_angles?.['L4-L5'] ? formatValue(currentLat.segmental_angles['L4-L5']) : '-',
          proposed: proposedLat?.segmental_angles?.['L4-L5'] ? formatValue(proposedLat.segmental_angles['L4-L5']) : '-',
          isSegmental: true
        },
        {
          label: 'L5-S1',
          current: currentLat?.segmental_angles?.['L5-S1'] ? formatValue(currentLat.segmental_angles['L5-S1']) : '-',
          proposed: proposedLat?.segmental_angles?.['L5-S1'] ? formatValue(proposedLat.segmental_angles['L5-S1']) : '-',
          isSegmental: true
        },
      ],
      sacroPelvic: [
        {
          label: 'Sacral Slope',
          current: currentLat?.sacral_slope ? formatValue(currentLat.sacral_slope) : '-',
          proposed: proposedLat?.sacral_slope ? formatValue(proposedLat.sacral_slope) : '-'
        },
        {
          label: 'Pelvic Incidence',
          current: currentLat?.pelvic_incidence ? formatValue(currentLat.pelvic_incidence) : '-',
          proposed: proposedLat?.pelvic_incidence ? formatValue(proposedLat.pelvic_incidence) : '-'
        },
        {
          label: 'Pelvic Tilt',
          current: currentLat?.pelvic_tilt ? formatValue(currentLat.pelvic_tilt) : '-',
          proposed: proposedLat?.pelvic_tilt ? formatValue(proposedLat.pelvic_tilt) : '-'
        },
      ],
      other: [
        {
          label: 'PI-LL Mismatch',
          current: currentCombined?.pi_ll_mismatch ? formatValue(currentCombined.pi_ll_mismatch) : '-',
          proposed: proposedCombined?.pi_ll_mismatch ? formatValue(proposedCombined.pi_ll_mismatch) : '-'
        },
      ],
    };

    return params;
  };

  const parameterData = buildParameterData();

  // Load X-ray images and measurements on component mount
  useEffect(() => {
    fetchXrayImages();
    fetchXrayMeasurements();
    fetchXrayKeypoints();
  }, []);

  // Compute adjustments when measurements are loaded or selected levels change
  useEffect(() => {
    if (xrayMeasurements) {
      computeAndFetchAdjustedMeasurements();
    }
  }, [selectedLevels, xrayMeasurements]);

  // Fetch X-ray images through the authed client as blobs — a bare <img src>
  // sends no Authorization header, so the /images/* endpoints always 401 it
  // and the uploaded X-rays never display.
  const fetchXrayImages = async () => {
    setLoading({ ap: true, lat: true });
    setErrors({ ap: null, lat: null });

    const fetchView = async (view, setImage) => {
      try {
        const response = await api.get(`${API_URL}/images/xray/${view}`, {
          responseType: 'blob'
        });
        setImage(URL.createObjectURL(response.data));
        return null;
      } catch (error) {
        // 404 just means no X-ray was uploaded — that's the empty state,
        // not an error.
        if (error.response?.status === 404) return null;
        console.error(`Error fetching ${view} X-ray image:`, error);
        return `Failed to load ${view.toUpperCase()} X-ray image`;
      }
    };

    const [apError, latError] = await Promise.all([
      fetchView('ap', setApImage),
      fetchView('lat', setLatImage)
    ]);
    setErrors({ ap: apError, lat: latError });
    setLoading({ ap: false, lat: false });
  };

  // Blob URLs hold the decoded images in memory until revoked
  useEffect(() => {
    return () => {
      [apImage, latImage].forEach(url => {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apImage, latImage]);

  // Function to fetch X-ray measurements
  const fetchXrayMeasurements = async () => {
    setMeasurementsLoading(true);

    try {
      const response = await api.get(`${API_URL}/xray-measurements`);
      console.log('X-ray measurements response:', response.data);

      const { ap, lat } = response.data;
      const hasData = response.data.status !== 'no_data' &&
        ((ap && Object.keys(ap).length > 0) || (lat && Object.keys(lat).length > 0));

      if (hasData) {
        setXrayMeasurements(response.data);
      } else {
        console.log('No X-ray measurements available');
        setXrayMeasurements(null);
      }
    } catch (error) {
      console.error('Error fetching X-ray measurements:', error);
      setXrayMeasurements(null);
    } finally {
      setMeasurementsLoading(false);
    }
  };

  // Function to fetch X-ray keypoints
  const fetchXrayKeypoints = async () => {
    try {
      const response = await api.get(`${API_URL}/xray-keypoints`);
      console.log('X-ray keypoints response:', response.data);

      if (response.data.status === 'success') {
        setXrayKeypoints(response.data);
      } else {
        console.log('No X-ray keypoints available');
        setXrayKeypoints(null);
      }
    } catch (error) {
      console.error('Error fetching X-ray keypoints:', error);
      setXrayKeypoints(null);
    }
  };

  // Function to convert vertebral levels to disc levels
  const convertToDiscLevels = (vertebralLevels) => {
    // Convert selected vertebral levels (e.g., L3, L4, L5) to disc levels (e.g., L3-L4, L4-L5)
    if (!vertebralLevels || vertebralLevels.length === 0) {
      return null; // No restriction, all levels allowed
    }

    const discLevels = [];
    const lumbarLevels = ['L1', 'L2', 'L3', 'L4', 'L5', 'S1'];

    // Sort the selected levels
    const sortedLevels = vertebralLevels
      .filter(level => lumbarLevels.includes(level))
      .sort((a, b) => lumbarLevels.indexOf(a) - lumbarLevels.indexOf(b));

    // Create disc levels between consecutive selected vertebrae
    for (let i = 0; i < sortedLevels.length - 1; i++) {
      const currentIdx = lumbarLevels.indexOf(sortedLevels[i]);
      const nextIdx = lumbarLevels.indexOf(sortedLevels[i + 1]);

      // Only add if they are consecutive
      if (nextIdx - currentIdx === 1) {
        discLevels.push(`${sortedLevels[i]}-${sortedLevels[i + 1]}`);
      }
    }

    console.log('Converted vertebral levels:', vertebralLevels, 'to disc levels:', discLevels);
    return discLevels.length > 0 ? discLevels : null;
  };

  // Function to compute alignment adjustments and fetch adjusted measurements
  const computeAndFetchAdjustedMeasurements = async () => {
    setAdjustedLoading(true);

    try {
      // Convert selected vertebral levels to disc levels
      const allowedDiscLevels = convertToDiscLevels(selectedLevels);

      // First, compute the alignment adjustments with allowed levels
      const adjustResponse = await api.post(`${API_URL}/xray-alignment-adjustment`, {
        allowed_levels: allowedDiscLevels
      });
      console.log('Alignment adjustment response:', adjustResponse.data);
      console.log('Allowed disc levels:', allowedDiscLevels);

      if (adjustResponse.data.status === 'success') {
        // Then fetch the adjusted measurements
        const measResponse = await api.get(`${API_URL}/xray-adjusted-measurements`);
        console.log('Adjusted measurements response:', measResponse.data);

        if (measResponse.data.status === 'success') {
          setAdjustedMeasurements(measResponse.data);
        } else {
          console.log('No adjusted measurements available');
          setAdjustedMeasurements(null);
        }
      }
    } catch (error) {
      console.error('Error computing/fetching adjusted measurements:', error);
      setAdjustedMeasurements(null);
    } finally {
      setAdjustedLoading(false);
    }
  };

  const toggleLines = () => setShowLines(!showLines);
  const toggleMeasurements = () => setShowMeasurements(!showMeasurements);

  const isValueChanged = (current, proposed) => {
    if (current === '-' || proposed === '-') return false;
    return current !== proposed;
  };

  const renderParameterSection = (title, params) => (
    <div style={styles.parameterSection}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {params.map((param, index) => {
        const isChanged = isValueChanged(param.current, param.proposed);

        return (
          <div key={index} style={{
            ...styles.parameterRow,
            ...(isChanged ? styles.parameterRowChanged : {}),
            ...(param.isSegmental ? styles.parameterRowSegmental : {})
          }}>
            <span style={{
              ...styles.parameterLabel,
              ...(param.isSegmental ? styles.parameterLabelSegmental : {})
            }}>{param.label}</span>
            <div style={styles.parameterValues}>
              <span style={styles.currentValue}>{param.current}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={styles.arrowIcon}>
                <path d="M5 12H19" stroke={theme.colors.neutral.text.secondary} strokeWidth="1.5" strokeLinecap="round" />
                <path d="M15 8L19 12L15 16" stroke={theme.colors.neutral.text.secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{
                ...styles.proposedValue,
                ...(adjustedLoading ? styles.proposedValueLoading : {})
              }}>{param.proposed}</span>
            </div>
            {isChanged && (
              <div style={styles.changeIndicator}></div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderAPOverlays = () => {
    if (!showLines || !xrayKeypoints?.ap || !apImageDims) return null;

    const { vertebrae, upper_vertebra_idx, lower_vertebra_idx } = xrayKeypoints.ap;
    const cobb_angle = xrayMeasurements?.ap?.cobb_angle;

    const { width: naturalWidth, height: naturalHeight } = apImageDims;

    return (
      <div style={styles.overlayContainer}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          {/* Draw vertebra boxes */}
          {vertebrae && vertebrae.map((vertebra, idx) => {
            const points = [
              vertebra.superiorLeft,
              vertebra.superiorRight,
              vertebra.inferiorRight,
              vertebra.inferiorLeft
            ].map(p => `${p[0]},${p[1]}`).join(' ');

            return (
              <g key={idx}>
                <polygon
                  points={points}
                  fill="none"
                  stroke="#00ff00"
                  strokeWidth="2"
                  opacity="0.8"
                />
                {showMeasurements && (
                  <text
                    x={vertebra.center?.[0] || ((vertebra.superiorLeft[0] + vertebra.inferiorRight[0]) / 2)}
                    y={vertebra.center?.[1] || ((vertebra.superiorLeft[1] + vertebra.inferiorRight[1]) / 2)}
                    fill="#00ff00"
                    fontSize="12"
                    textAnchor="middle"
                    opacity="0.8"
                  >
                    V{vertebrae.length - idx}
                  </text>
                )}
              </g>
            );
          })}

          {/* Draw Cobb angle lines if we have the tilted vertebrae */}
          {upper_vertebra_idx !== undefined && lower_vertebra_idx !== undefined && vertebrae && (() => {
            const upperVert = vertebrae[upper_vertebra_idx];
            const lowerVert = vertebrae[lower_vertebra_idx];

            // Calculate extended endplate lines (scale_factor = 2.0 like in Python)
            const scaleFactor = 2.0;

            // Upper vertebra - superior endplate
            const upperDx = upperVert.superiorRight[0] - upperVert.superiorLeft[0];
            const upperDy = upperVert.superiorRight[1] - upperVert.superiorLeft[1];
            const upperStart = [
              upperVert.superiorLeft[0] - upperDx * scaleFactor,
              upperVert.superiorLeft[1] - upperDy * scaleFactor
            ];
            const upperEnd = [
              upperVert.superiorRight[0] + upperDx * scaleFactor,
              upperVert.superiorRight[1] + upperDy * scaleFactor
            ];

            // Lower vertebra - inferior endplate
            const lowerDx = lowerVert.inferiorRight[0] - lowerVert.inferiorLeft[0];
            const lowerDy = lowerVert.inferiorRight[1] - lowerVert.inferiorLeft[1];
            const lowerStart = [
              lowerVert.inferiorLeft[0] - lowerDx * scaleFactor,
              lowerVert.inferiorLeft[1] - lowerDy * scaleFactor
            ];
            const lowerEnd = [
              lowerVert.inferiorRight[0] + lowerDx * scaleFactor,
              lowerVert.inferiorRight[1] + lowerDy * scaleFactor
            ];

            // Calculate perpendicular lines
            const upperMidX = (upperVert.superiorLeft[0] + upperVert.superiorRight[0]) / 2;
            const upperMidY = (upperVert.superiorLeft[1] + upperVert.superiorRight[1]) / 2;
            const upperPerpDx = -upperDy;
            const upperPerpDy = upperDx;
            const upperMag = Math.sqrt(upperPerpDx * upperPerpDx + upperPerpDy * upperPerpDy);
            const upperScale = 200 / upperMag;

            const lowerMidX = (lowerVert.inferiorLeft[0] + lowerVert.inferiorRight[0]) / 2;
            const lowerMidY = (lowerVert.inferiorLeft[1] + lowerVert.inferiorRight[1]) / 2;
            const lowerPerpDx = -lowerDy;
            const lowerPerpDy = lowerDx;
            const lowerMag = Math.sqrt(lowerPerpDx * lowerPerpDx + lowerPerpDy * lowerPerpDy);
            const lowerScale = 200 / lowerMag;

            return (
              <>
                {/* Upper vertebra endplate line (extended) */}
                <line
                  x1={upperStart[0]}
                  y1={upperStart[1]}
                  x2={upperEnd[0]}
                  y2={upperEnd[1]}
                  stroke="#ff0000"
                  strokeWidth="4"
                  opacity="0.8"
                />

                {/* Lower vertebra endplate line (extended) */}
                <line
                  x1={lowerStart[0]}
                  y1={lowerStart[1]}
                  x2={lowerEnd[0]}
                  y2={lowerEnd[1]}
                  stroke="#0000ff"
                  strokeWidth="4"
                  opacity="0.8"
                />

                {/* Upper perpendicular line (dashed) */}
                <line
                  x1={upperMidX - upperPerpDx * upperScale}
                  y1={upperMidY - upperPerpDy * upperScale}
                  x2={upperMidX + upperPerpDx * upperScale}
                  y2={upperMidY + upperPerpDy * upperScale}
                  stroke="#ff0000"
                  strokeWidth="4"
                  strokeDasharray="10,10"
                  opacity="0.8"
                />

                {/* Lower perpendicular line (dashed) */}
                <line
                  x1={lowerMidX - lowerPerpDx * lowerScale}
                  y1={lowerMidY - lowerPerpDy * lowerScale}
                  x2={lowerMidX + lowerPerpDx * lowerScale}
                  y2={lowerMidY + lowerPerpDy * lowerScale}
                  stroke="#0000ff"
                  strokeWidth="4"
                  strokeDasharray="10,10"
                  opacity="0.8"
                />
              </>
            );
          })()}

          {/* Cobb angle measurement */}
          {showMeasurements && cobb_angle && (
            <g>
              <rect
                x={300}
                y={200}
                width={80}
                height={40}
                fill="rgba(0,0,0,0.7)"
                rx="4"
              />
              <text
                x={340}
                y={220}
                fill="#ffffff"
                fontSize="16"
                fontWeight="bold"
                textAnchor="middle"
              >
                {cobb_angle.value}°
              </text>
              {cobb_angle.severity && (
                <text
                  x={340}
                  y={235}
                  fill="#ffffff"
                  fontSize="10"
                  textAnchor="middle"
                >
                  {cobb_angle.severity}
                </text>
              )}
            </g>
          )}
        </svg>
      </div>
    );
  };

  const renderLATOverlays = () => {
    if (!showLines || !xrayKeypoints?.lat || !latImageDims) return null;

    const { keypoints } = xrayKeypoints.lat;
    if (!keypoints || keypoints.length === 0) return null;

    const { width: naturalWidth, height: naturalHeight } = latImageDims;

    const lineScaleFactor = 4;  // Smaller lines and dots
    const textScaleFactor = 10; // Bigger text

    // Helper functions
    const getPoint = (idx) => keypoints[idx];
    const getMidpoint = (p1, p2) => [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];

    // Get key points
    const c7 = getPoint(0);
    const s1_anterior = getPoint(2);
    const s1_posterior = getPoint(3);
    const l1_anterior = getPoint(4);
    const l1_posterior = getPoint(5);
    const l_hip = getPoint(7);
    const r_hip = getPoint(8);

    const s1_midpoint = getMidpoint(s1_anterior, s1_posterior);
    const hip_center = getMidpoint(l_hip, r_hip);

    return (
      <div style={styles.overlayContainer}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          {/* SVA - Vertical line from C7 */}
          <line
            x1={c7[0]}
            y1={0}
            x2={c7[0]}
            y2={naturalHeight}
            stroke="#2196f3"
            strokeWidth="20"
            opacity="0.8"
          />

          {/* SVA horizontal measurement line */}
          <line
            x1={c7[0]}
            y1={s1_posterior[1]}
            x2={s1_posterior[0]}
            y2={s1_posterior[1]}
            stroke="#2196f3"
            strokeWidth="20"
            opacity="0.8"
          />

          {/* PT - Pelvic Tilt lines */}
          <line
            x1={hip_center[0]}
            y1={hip_center[1]}
            x2={s1_midpoint[0]}
            y2={s1_midpoint[1]}
            stroke="#ff0000"
            strokeWidth="20"
            opacity="0.8"
          />
          <line
            x1={hip_center[0]}
            y1={hip_center[1]}
            x2={hip_center[0]}
            y2={hip_center[1] - 200}
            stroke="#ff0000"
            strokeWidth="20"
            opacity="0.8"
          />

          {/* SS - Sacral Slope lines */}
          <line
            x1={s1_anterior[0]}
            y1={s1_anterior[1]}
            x2={s1_posterior[0]}
            y2={s1_posterior[1]}
            stroke="#ff0000"
            strokeWidth="20"
            opacity="0.8"
          />
          <line
            x1={s1_anterior[0]}
            y1={s1_anterior[1]}
            x2={s1_anterior[0] - 200}
            y2={s1_anterior[1]}
            stroke="#ff0000"
            strokeWidth="20"
            opacity="0.8"
          />

          {/* PI - Pelvic Incidence lines */}
          <line
            x1={s1_midpoint[0]}
            y1={s1_midpoint[1]}
            x2={s1_midpoint[0] - 100}
            y2={s1_midpoint[1] - 100}
            stroke="#ff0000"
            strokeWidth="20"
            strokeDasharray={`${12 * lineScaleFactor},${12 * lineScaleFactor}`}
            opacity="0.8"
          />
          <line
            x1={s1_midpoint[0]}
            y1={s1_midpoint[1]}
            x2={hip_center[0]}
            y2={hip_center[1]}
            stroke="#ff0000"
            strokeWidth="20"
            opacity="0.8"
          />

          {/* LL - Lumbar Lordosis lines */}
          <line
            x1={l1_anterior[0]}
            y1={l1_anterior[1]}
            x2={l1_posterior[0] + 50}
            y2={l1_posterior[1]}
            stroke="#ff00ff"
            strokeWidth="20"
            opacity="0.8"
          />
          <line
            x1={s1_anterior[0]}
            y1={s1_anterior[1]}
            x2={s1_posterior[0] + 50}
            y2={s1_posterior[1]}
            stroke="#ff00ff"
            strokeWidth="20"
            opacity="0.8"
          />
          <line
            x1={l1_posterior[0] + 50}
            y1={l1_posterior[1]}
            x2={s1_posterior[0] + 50}
            y2={s1_posterior[1]}
            stroke="#ff00ff"
            strokeWidth="20"
            strokeDasharray={`${12 * lineScaleFactor},${12 * lineScaleFactor}`}
            opacity="0.8"
          />

          {/* Segmental angle lines if available */}
          {keypoints.length > 26 && [
            { start: 9, end: 10, nextStart: 11, nextEnd: 12, label: 'L1-L2' },
            { start: 13, end: 14, nextStart: 15, nextEnd: 16, label: 'L2-L3' },
            { start: 17, end: 18, nextStart: 19, nextEnd: 20, label: 'L3-L4' },
            { start: 21, end: 22, nextStart: 23, nextEnd: 24, label: 'L4-L5' },
            { start: 25, end: 26, nextStart: 2, nextEnd: 3, label: 'L5-S1' },
          ].map((seg) => {
            const p1 = getPoint(seg.start);
            const p2 = getPoint(seg.end);
            const p3 = getPoint(seg.nextStart);
            const p4 = getPoint(seg.nextEnd);

            return (
              <g key={seg.label}>
                <line
                  x1={p1[0]}
                  y1={p1[1]}
                  x2={p2[0]}
                  y2={p2[1]}
                  stroke="#ff00ff"
                  strokeWidth="20"
                  opacity="0.8"
                />
                <line
                  x1={p3[0]}
                  y1={p3[1]}
                  x2={p4[0]}
                  y2={p4[1]}
                  stroke="#ff00ff"
                  strokeWidth="20"
                  opacity="0.8"
                />
                <line
                  x1={p2[0]}
                  y1={p2[1]}
                  x2={p4[0]}
                  y2={p4[1]}
                  stroke="#ff00ff"
                  strokeWidth="20"
                  strokeDasharray={`${12 * lineScaleFactor},${12 * lineScaleFactor}`}
                  opacity="0.8"
                />
              </g>
            );
          })}


          {/* Measurement labels */}
          {showMeasurements && (
            <>
              {xrayMeasurements?.lat?.sva && (
                <g>
                  <rect
                    x={c7[0] + 10 * textScaleFactor}
                    y={s1_posterior[1] - 20 * textScaleFactor}
                    width={80 * textScaleFactor}
                    height={25 * textScaleFactor}
                    fill="rgba(0,0,0,0.7)"
                    rx="4"
                  />
                  <text
                    x={c7[0] + 50 * textScaleFactor}
                    y={s1_posterior[1] - 5 * textScaleFactor}
                    fill="#2196f3"
                    fontSize={12 * textScaleFactor}
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    SVA: {xrayMeasurements.lat.sva.value}{xrayMeasurements.lat.sva.unit}
                  </text>
                </g>
              )}

              {xrayMeasurements?.lat?.pelvic_tilt && (
                <g>
                  <rect
                    x={hip_center[0] - 80 * textScaleFactor}
                    y={hip_center[1] - 40 * textScaleFactor}
                    width={60 * textScaleFactor}
                    height={25 * textScaleFactor}
                    fill="rgba(0,0,0,0.7)"
                    rx="4"
                  />
                  <text
                    x={hip_center[0] - 50 * textScaleFactor}
                    y={hip_center[1] - 25 * textScaleFactor}
                    fill="#ff4081"
                    fontSize={12 * textScaleFactor}
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    PT: {xrayMeasurements.lat.pelvic_tilt.value}°
                  </text>
                </g>
              )}

              {xrayMeasurements?.lat?.sacral_slope && (
                <g>
                  <rect
                    x={s1_anterior[0] - 100 * textScaleFactor}
                    y={s1_anterior[1] - 20 * textScaleFactor}
                    width={60 * textScaleFactor}
                    height={25 * textScaleFactor}
                    fill="rgba(0,0,0,0.7)"
                    rx="4"
                  />
                  <text
                    x={s1_anterior[0] - 70 * textScaleFactor}
                    y={s1_anterior[1] - 5 * textScaleFactor}
                    fill="#ff4081"
                    fontSize={12 * textScaleFactor}
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    SS: {xrayMeasurements.lat.sacral_slope.value}°
                  </text>
                </g>
              )}

              {xrayMeasurements?.lat?.pelvic_incidence && (
                <g>
                  <rect
                    x={s1_midpoint[0] + 10 * textScaleFactor}
                    y={s1_midpoint[1] - 40 * textScaleFactor}
                    width={60 * textScaleFactor}
                    height={25 * textScaleFactor}
                    fill="rgba(0,0,0,0.7)"
                    rx="4"
                  />
                  <text
                    x={s1_midpoint[0] + 40 * textScaleFactor}
                    y={s1_midpoint[1] - 25 * textScaleFactor}
                    fill="#ff4081"
                    fontSize={12 * textScaleFactor}
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    PI: {xrayMeasurements.lat.pelvic_incidence.value}°
                  </text>
                </g>
              )}

              {xrayMeasurements?.lat?.lumbar_lordosis && (
                <g>
                  <rect
                    x={(l1_posterior[0] + s1_posterior[0]) / 2 + 60 * textScaleFactor}
                    y={(l1_posterior[1] + s1_posterior[1]) / 2 - 10 * textScaleFactor}
                    width={60 * textScaleFactor}
                    height={25 * textScaleFactor}
                    fill="rgba(0,0,0,0.7)"
                    rx="4"
                  />
                  <text
                    x={(l1_posterior[0] + s1_posterior[0]) / 2 + 90 * textScaleFactor}
                    y={(l1_posterior[1] + s1_posterior[1]) / 2 + 5 * textScaleFactor}
                    fill="#ff00ff"
                    fontSize={12 * textScaleFactor}
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    LL: {xrayMeasurements.lat.lumbar_lordosis.value}°
                  </text>
                </g>
              )}

            </>
          )}

          {/* Draw all keypoints */}
          {keypoints.map((point, idx) => (
            <circle
              key={idx}
              cx={point[0]}
              cy={point[1]}
              r="24"
              fill="#00ff00"
              opacity="0.8"
            />
          ))}
        </svg>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.mainContent}>
        {/* Left side - X-Ray Images */}
        <div style={styles.imagesContainer}>
          {/* AP X-ray Image */}
          <div className="viewer rise-in" style={{ ...styles.viewerCard, '--i': 0 }}>
            <div className="viewer__hud" style={styles.viewerLabel}>
              AP X-ray (Coronal)
            </div>
            <div style={styles.imageWrapper}>
              {loading.ap ? (
                <div style={styles.stateContainer}>
                  <div className="spinner" aria-hidden="true"></div>
                  <p style={styles.loadingText}>Loading AP image...</p>
                </div>
              ) : errors.ap ? (
                <div style={styles.stateContainer}>
                  <div className="alert alert--danger" role="alert" style={styles.errorAlert}>
                    {errors.ap}
                  </div>
                </div>
              ) : apImage ? (
                <div style={styles.imageWithOverlay}>
                  <img
                    src={apImage}
                    alt="AP X-ray"
                    style={styles.xrayImage}
                    onLoad={(e) => {
                      setApImageDims({ width: e.target.naturalWidth, height: e.target.naturalHeight });
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      setErrors(prev => ({ ...prev, ap: 'Failed to load AP X-ray image' }));
                    }}
                  />
                  {renderAPOverlays()}
                </div>
              ) : (
                <div style={styles.stateContainer}>
                  <p style={styles.loadingText}>No AP X-ray uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* LAT X-ray Image */}
          <div className="viewer rise-in" style={{ ...styles.viewerCard, '--i': 1 }}>
            <div className="viewer__hud" style={styles.viewerLabel}>
              LAT X-ray (Sagittal)
            </div>
            <div style={styles.imageWrapper}>
              {loading.lat ? (
                <div style={styles.stateContainer}>
                  <div className="spinner" aria-hidden="true"></div>
                  <p style={styles.loadingText}>Loading LAT image...</p>
                </div>
              ) : errors.lat ? (
                <div style={styles.stateContainer}>
                  <div className="alert alert--danger" role="alert" style={styles.errorAlert}>
                    {errors.lat}
                  </div>
                </div>
              ) : latImage ? (
                <div style={styles.imageWithOverlay}>
                  <img
                    src={latImage}
                    alt="LAT X-ray"
                    style={styles.xrayImage}
                    onLoad={(e) => {
                      setLatImageDims({ width: e.target.naturalWidth, height: e.target.naturalHeight });
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      setErrors(prev => ({ ...prev, lat: 'Failed to load LAT X-ray image' }));
                    }}
                  />
                  {renderLATOverlays()}
                </div>
              ) : (
                <div style={styles.stateContainer}>
                  <p style={styles.loadingText}>No LAT X-ray uploaded</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Parameters */}
        <div className="card card--flush rise-in" style={{ ...styles.parametersPanel, '--i': 2 }}>
          <div style={styles.parametersHeader}>
            <div style={styles.parametersTitle}>
              Parameter Comparison

              <div style={styles.displayButtonsContainer}>
                <button
                  type="button"
                  className={`icon-btn${showLines ? ' icon-btn--active' : ''}`}
                  onClick={toggleLines}
                  title="Toggle Alignment Lines"
                  aria-label="Toggle alignment lines"
                  aria-pressed={showLines}
                >
                  <svg style={styles.optionIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M3 21L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`icon-btn${showMeasurements ? ' icon-btn--active' : ''}`}
                  onClick={toggleMeasurements}
                  title="Toggle Measurements"
                  aria-label="Toggle measurements"
                  aria-pressed={showMeasurements}
                >
                  <svg style={styles.optionIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20V20H4V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M12 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div style={styles.legendContainer}>
            <div style={styles.legendExample}>
              <span style={styles.legendParameter}>Parameter</span>
              <div style={styles.legendValues}>
                <span style={styles.legendCurrentValue}>XX°</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={styles.legendArrow}>
                  <path d="M5 12H19" stroke={theme.colors.neutral.text.secondary} strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M15 8L19 12L15 16" stroke={theme.colors.neutral.text.secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={styles.legendProposedValue}>XX°</span>
              </div>
            </div>
            <div style={styles.legendNote}>
              <div style={styles.recommendIndicator}></div>
              <span style={styles.legendText}>Indicates recommended adjustment</span>
            </div>
          </div>

          <div style={styles.parametersContent}>
            {!measurementsLoading && !xrayMeasurements && (
              <div style={styles.comingSoonNotice}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={styles.comingSoonIcon} xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12L15 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                </svg>
                <div>
                  <div style={styles.comingSoonTitle}>X-ray measurement analysis coming soon</div>
                  <div style={styles.comingSoonSub}>
                    Automatic keypoint detection for X-rays is in development.
                    Uploaded images display here now; alignment parameters will
                    populate once analysis ships.
                  </div>
                </div>
              </div>
            )}
            {measurementsLoading && (
              <div style={styles.measurementsLoading}>
                <div className="spinner spinner--sm" aria-hidden="true"></div>
                Loading measurements...
              </div>
            )}
            {!measurementsLoading && (
              <>
                {renderParameterSection('SAGITTAL VERTEBRAL AXIS', parameterData.sagittalVertebralAxis)}
                {renderParameterSection('COBB ANGLE', parameterData.cobbAngle)}
                {renderParameterSection('LUMBAR', parameterData.lumbar)}
                {renderParameterSection('SACRO-PELVIC', parameterData.sacroPelvic)}
                {renderParameterSection('OTHER', parameterData.other)}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-canvas)',
    overflow: 'hidden',
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    gap: 'var(--space-4)',
    padding: 'var(--space-4) clamp(var(--space-4), 2.5vw, var(--space-5))',
  },
  imagesContainer: {
    flex: '3 1 0%',
    minWidth: 0,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 'var(--space-3)',
  },
  viewerCard: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
  },
  viewerLabel: {
    top: 10,
    left: 10,
    color: 'var(--text-primary)',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
  },
  imageWrapper: {
    flex: 1,
    minHeight: 0,
    backgroundColor: 'var(--bg-viewer)',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageWithOverlay: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  xrayImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  stateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-4)',
    textAlign: 'center',
  },
  loadingText: {
    margin: 0,
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
  },
  errorAlert: {
    fontSize: '0.82rem',
  },

  // Overlay styles
  overlayContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Parameters panel
  parametersPanel: {
    flex: '2 1 0%',
    minWidth: 'min(320px, 100%)',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  parametersHeader: {
    flexShrink: 0,
    padding: 'var(--space-3) var(--space-4)',
    borderBottom: '1px solid var(--line-subtle)',
  },
  parametersTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-3)',
    color: 'var(--text-primary)',
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
  },
  displayButtonsContainer: {
    display: 'flex',
    gap: 'var(--space-1)',
    marginLeft: 'auto',
  },
  optionIcon: {
    width: 16,
    height: 16,
  },
  legendContainer: {
    flexShrink: 0,
    padding: 'var(--space-2) var(--space-4) var(--space-3)',
    borderBottom: '1px solid var(--line-subtle)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
  },
  legendExample: {
    display: 'flex',
    alignItems: 'center',
    opacity: 0.6,
    paddingLeft: 12, // Match parameter row padding
  },
  legendParameter: {
    flex: '0 0 45%',
    minWidth: 0,
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    fontStyle: 'italic',
  },
  legendValues: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: 'var(--space-2)',
  },
  legendCurrentValue: {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
    minWidth: 44,
    textAlign: 'right',
    fontStyle: 'italic',
  },
  legendProposedValue: {
    fontSize: '0.75rem',
    color: 'var(--accent)',
    fontFamily: 'var(--font-mono)',
    minWidth: 44,
    fontStyle: 'italic',
    opacity: 0.8,
  },
  legendArrow: {
    opacity: 0.3,
    flexShrink: 0,
  },
  legendNote: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-1)',
    paddingLeft: 12, // Match parameter row padding
  },
  legendText: {
    fontSize: '0.7rem',
    color: 'var(--text-secondary)',
    fontWeight: 400,
  },
  recommendIndicator: {
    width: 3,
    height: 14,
    flexShrink: 0,
    backgroundColor: 'var(--accent)',
    borderRadius: 1.5,
    opacity: 0.8,
  },
  parametersContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 'var(--space-2) var(--space-4) var(--space-3)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-3)',
  },
  measurementsLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 'var(--space-4)',
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
  },
  comingSoonNotice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    margin: 'var(--space-3) var(--space-4) var(--space-2)',
    padding: '12px 14px',
    background: 'var(--accent-soft)',
    border: '1px solid rgba(79, 156, 240, 0.25)',
    borderRadius: 'var(--radius-md)',
  },
  comingSoonIcon: {
    color: 'var(--accent)',
    flexShrink: 0,
    marginTop: '2px',
  },
  comingSoonTitle: {
    fontSize: '0.84rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '3px',
  },
  comingSoonSub: {
    fontSize: '0.78rem',
    lineHeight: 1.5,
    color: 'var(--text-secondary)',
  },
  parameterSection: {
    minWidth: 0,
  },
  sectionTitle: {
    fontSize: '0.68rem',
    fontWeight: 600,
    color: 'var(--text-tertiary)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    margin: '0 0 6px',
  },
  parameterRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px 6px 12px',
    borderBottom: '1px solid var(--line-subtle)',
    borderRadius: 'var(--radius-xs)',
    transition: 'background var(--dur-fast) ease',
  },
  parameterRowChanged: {
    backgroundColor: 'var(--accent-softer)',
  },
  parameterRowSegmental: {
    // No extra padding needed - handled in label
  },
  parameterLabel: {
    flex: '0 0 45%',
    minWidth: 0,
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  parameterLabelSegmental: {
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
    paddingLeft: 24, // Indent only the label
  },
  parameterValues: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: 'var(--space-2)',
  },
  currentValue: {
    fontSize: '0.85rem',
    color: 'var(--text-primary)',
    fontWeight: 500,
    fontFamily: 'var(--font-mono)',
    letterSpacing: '-0.02em',
    minWidth: 44,
    textAlign: 'right',
  },
  proposedValue: {
    fontSize: '0.85rem',
    color: 'var(--accent)',
    fontWeight: 500,
    fontFamily: 'var(--font-mono)',
    letterSpacing: '-0.02em',
    minWidth: 44,
    transition: 'opacity var(--dur-fast) ease',
  },
  proposedValueLoading: {
    opacity: 0.35,
  },
  arrowIcon: {
    opacity: 0.3,
    flexShrink: 0,
  },
  changeIndicator: {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 3,
    height: '70%',
    backgroundColor: 'var(--accent)',
    borderRadius: 1.5,
    opacity: 0.9,
  },
};

export default AlignmentPage;
